import type { KeyboardEvent } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { CardDto } from '../../lib/types';
import { formatBuildCost, formatRoi, formatShortDate } from '../../lib/format';
import { buildCost } from '../../lib/roi';
import { MONTHS, monthInfo } from '../../lib/months';
import { useHighlight } from './highlight';

export type SetMonth = (cardId: number, month: number | null) => void;

const tileClass =
  'bg-white border border-slate-200 rounded-md px-2.5 py-2 text-sm shadow-sm cursor-pointer';

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/**
 * Static tile body: bold navy title, a green ROI badge when ROI is set, and one muted meta line
 * ("2 devs · 6 wks · $12,000 build", plus a navy "Due 3 Dec" while the card has an expected date).
 */
export function CardTileBody({ card, onSetMonth }: { card: CardDto; onSetMonth?: SetMonth }) {
  const roi = formatRoi(card);
  const cost = buildCost(card);
  const meta = [plural(card.workersNeeded, 'dev', 'devs'), plural(card.estimateWeeks, 'wk', 'wks')];
  if (cost > 0) meta.push(`${formatBuildCost(cost)} build`);
  return (
    <>
      <div className="font-semibold text-atg-navy leading-snug line-clamp-2 break-words">{card.title}</div>
      <div className="mt-1 flex flex-wrap items-center gap-1">
        {roi && (
          <div className="inline-block rounded bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 text-[11px] font-semibold text-emerald-800">
            {roi}
          </div>
        )}
        <MonthPill card={card} onSetMonth={onSetMonth} />
      </div>
      <div className="mt-1 text-[11px] text-slate-500 leading-tight">
        {meta.join(' · ')}
        {card.expectedDate && (
          <>
            {' · '}
            <span className="font-semibold text-atg-navy">Due {formatShortDate(card.expectedDate)}</span>
          </>
        )}
      </div>
      {card.dependencyCardId != null && <DependencyChip card={card} />}
    </>
  );
}

/**
 * Month pill. Write users get a native dropdown laid invisibly over the pill, so a click opens the
 * month list without starting a drag or opening the dialog. Read users just see the coloured pill.
 */
export function MonthPill({ card, onSetMonth }: { card: CardDto; onSetMonth?: SetMonth }) {
  const month = monthInfo(card.targetMonth);
  if (!onSetMonth && !month) return null;
  const style = month
    ? { backgroundColor: month.color, color: month.text }
    : { backgroundColor: '#f1f5f9', color: '#64748b', border: '1px dashed #cbd5e1' };
  return (
    <span
      className={`relative inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold leading-tight ${
        onSetMonth ? 'focus-within:ring-2 ring-atg-blue' : ''
      }`}
      style={style}
      title={month ? month.long : undefined}
      data-month-pill={card.id}
    >
      {month ? month.short : 'Month'}
      {onSetMonth && <span aria-hidden="true" className="text-[9px]">▾</span>}
      {onSetMonth && (
        <select
          aria-label={`Month for ${card.title}`}
          value={card.targetMonth ?? ''}
          onChange={(e) => onSetMonth(card.id, e.target.value === '' ? null : Number(e.target.value))}
          onPointerDown={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          // The open list inherits the select's colours, not the pill's, so pin dark-on-white here.
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer text-slate-900 bg-white"
          style={{ color: '#0f172a', backgroundColor: '#ffffff' }}
        >
          <option value="" className="text-slate-900 bg-white">No month</option>
          {MONTHS.map((m) => (
            <option key={m.n} value={m.n} className="text-slate-900 bg-white">{m.long}</option>
          ))}
        </select>
      )}
    </span>
  );
}

/** "Depends on: X" chip; hovering it makes the dependency's tile glow wherever it sits on the board. */
function DependencyChip({ card }: { card: CardDto }) {
  const { setHighlightedCardId } = useHighlight();
  const id = card.dependencyCardId as number;
  return (
    <div
      className="mt-1 inline-flex items-center gap-1 rounded border border-atg-accent/60 bg-orange-50 px-1.5 py-0.5 text-[11px] text-atg-navy cursor-help"
      onMouseEnter={() => setHighlightedCardId(id)}
      onMouseLeave={() => setHighlightedCardId(null)}
      onFocus={() => setHighlightedCardId(id)}
      onBlur={() => setHighlightedCardId(null)}
      tabIndex={0}
      title={`Depends on: ${card.dependencyCardTitle ?? ''}`}
    >
      <span aria-hidden="true">↳</span>
      <span className="truncate max-w-[14rem]">
        Depends on: <span className="font-semibold">{card.dependencyCardTitle ?? '…'}</span>
      </span>
    </div>
  );
}

/** Clone that follows the pointer inside `DragOverlay`. */
export function CardTileOverlay({ card }: { card: CardDto }) {
  return (
    <div className={`${tileClass} shadow-lg`}>
      <CardTileBody card={card} />
    </div>
  );
}

interface CardTileProps {
  card: CardDto;
  canWrite: boolean;
  onOpen: (cardId: number) => void;
  /** Write users: change the card's month from the tile's pill. Omitted = pill is display-only. */
  onSetMonth?: SetMonth;
}

export function CardTile({ card, canWrite, onOpen, onSetMonth }: CardTileProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: card.id,
    disabled: !canWrite,
    // Snap into place on drop; the default animates the tile from its old slot to the new one.
    animateLayoutChanges: () => false,
  });
  const { highlightedCardId } = useHighlight();

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    // Space picks up / drops (KeyboardSensor); Enter opens the dialog.
    if (!isDragging && e.key === 'Enter') {
      e.preventDefault();
      onOpen(card.id);
      return;
    }
    listeners?.onKeyDown?.(e);
  }

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      {...(canWrite ? attributes : {})}
      {...listeners}
      onKeyDown={onKeyDown}
      onClick={() => onOpen(card.id)}
      tabIndex={0}
      role="button"
      aria-label={card.title}
      className={`${tileClass} hover:border-atg-blue hover:shadow-md focus:outline-none focus-visible:ring-2 ring-atg-blue transition-shadow ${
        isDragging ? 'opacity-40' : ''
      } ${
        highlightedCardId === card.id
          ? 'ring-2 ring-atg-accent border-atg-accent dep-glow'
          : ''
      }`}
    >
      <CardTileBody card={card} onSetMonth={canWrite ? onSetMonth : undefined} />
    </div>
  );
}
